import { useCallback, useMemo, useRef, useState } from "react";
import { Badge } from "react-bootstrap";
import styles from "./ControlTags.module.css";
import { DropdownItem, DropdownPortal } from "./DropdownPortal";

/**
 * Props for the ControlTags component.
 */
export interface ControlTagsProps {
  /**
   * Whether creating new tags via the dropdown "Create:" option is allowed.
   * This adds a special dropdown entry when the search text is a novel value.
   */
  allowCreate?: boolean;
  /**
   * Whether clicking an existing chip enters inline edit mode, allowing the
   * user to rename or remove (by clearing) the tag in place.
   */
  allowEdit?: boolean;
  /**
   * Whether typing free-text and pressing Enter creates a tag directly without
   * requiring dropdown interaction. When enabled in the absence of available
   * options, the control operates in a pure free-text entry mode with
   * commit-on-blur behaviour.
   */
  automaticallyCreateOnReturn?: boolean;
  /**
   * List of available tag options shown in the dropdown.
   */
  availableOptions: string[];
  /**
   * Optional custom CSS class applied to the outer wrapper element.
   */
  className?: string;
  /**
   * Whether the control is disabled. When true, the dropdown cannot be opened,
   * chip remove buttons are hidden, and editing is suppressed; chips remain visible.
   */
  disabled?: boolean;
  /**
   * Fixed height of the chip input area. Ensures stable layout regardless of
   * content. Defaults to "80px".
   */
  height?: string;
  /**
   * Customisable aria-label for the primary text input. Defaults to "Search tags".
   */
  inputAriaLabel?: string;
  /**
   * Callback invoked with the full updated selection when tags change.
   *
   * @param selectedTags - The new set of selected tags.
   */
  onSelectionChange: (selectedTags: string[]) => void;
  /**
   * Placeholder text shown inside the input when no tags are selected.
   */
  placeholder?: string;
  /**
   * Currently selected tags (controlled).
   */
  selectedTags: string[];
  /**
   * Whether to show checkboxes next to dropdown items indicating selection state.
   * Defaults to true.
   */
  showCheckboxes?: boolean;
}

/**
 * A multi-select tag control that renders selected values as chips inside a fixed-size,
 * scrollable input area. Features include:
 * - Inline chip rendering inside a form-control container
 * - Optional persistent dropdown with checkboxes for multi-select
 * - Optional free-text tag creation via Enter key (no dropdown required)
 * - Optional inline chip editing by clicking an existing chip
 * - Click-outside-to-close, keyboard navigation, and optional dropdown tag creation
 *
 * The component is fully controlled — display derives from `selectedTags`; changes are
 * communicated via `onSelectionChange` with the full updated array.
 *
 * @param props - Component props.
 * @param props.allowCreate - Allows adding tags via dropdown "Create:" option (defaults to false).
 * @param props.allowEdit - Allows inline chip editing on click (defaults to false).
 * @param props.automaticallyCreateOnReturn - Allows free-text tag creation via Enter (defaults to false).
 * @param props.availableOptions - Available tag options shown in the dropdown.
 * @param props.className - Custom CSS class for the outer wrapper.
 * @param props.disabled - Disables the control (defaults to false).
 * @param props.height - Fixed height of the input area (defaults to "80px").
 * @param props.inputAriaLabel - Aria-label for the primary input (defaults to "Search tags").
 * @param props.onSelectionChange - Callback when the selection changes.
 * @param props.placeholder - Placeholder shown when no tags are selected (defaults to "Select...").
 * @param props.selectedTags - Currently selected tags (controlled).
 * @param props.showCheckboxes - Whether to show checkboxes in the dropdown (defaults to true).
 * @returns A multi-select tag input with inline chips and optional dropdown or free-text entry.
 */
export const ControlTags = ({
  allowCreate = false,
  allowEdit = false,
  automaticallyCreateOnReturn = false,
  availableOptions,
  className,
  disabled = false,
  height = "80px",
  inputAriaLabel = "Search tags",
  onSelectionChange,
  placeholder = "Select...",
  selectedTags,
  showCheckboxes = true,
}: ControlTagsProps) => {
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  /** Index of the chip currently being edited inline, or null when not editing. */
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  /** Current text value of the chip being edited inline. */
  const [editValue, setEditValue] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /** Whether the dropdown has content to display (options or create capability). */
  const hasDropdown = availableOptions.length > 0 || allowCreate;

  /**
   * The control is effectively disabled when explicitly disabled OR when there
   * are no available options, creation is not allowed, and free-text entry is not allowed.
   */
  const effectivelyDisabled = disabled || (!hasDropdown && !automaticallyCreateOnReturn);

  /**
   * Whether the input should commit its value on blur. This is only true in
   * pure free-text mode (no dropdown present) to avoid blur-triggered commits
   * conflicting with dropdown item selection.
   */
  const commitOnBlur = automaticallyCreateOnReturn && !hasDropdown;

  /**
   * Build dropdown items from the union of available options and currently-selected tags,
   * filtered by the current search text. Each item carries a `checked` flag so the dropdown
   * renders a checkbox indicating whether the tag is already selected.
   * An optional "Create" entry is prepended when `allowCreate` is on and the search text
   * is a novel value.
   */
  const dropdownItems = useMemo((): DropdownItem[] => {
    if (!hasDropdown) {
      return [];
    }

    // Union of available options and any orphaned selected tags not in the options list
    const allOptions = [...new Set([...availableOptions, ...selectedTags])];
    const filtered = allOptions.filter((option) =>
      option.toLowerCase().includes(searchText.toLowerCase()),
    );

    const items: DropdownItem[] = filtered.map((option, index) => ({
      id: `option-${index}`,
      text: option,
      ...(showCheckboxes ? { checked: selectedTags.includes(option) } : {}),
    }));

    // Prepend a "Create" entry when allowCreate is true and the search text is a novel value
    if (
      allowCreate &&
      searchText.trim() &&
      !allOptions.some((opt) => opt.toLowerCase() === searchText.toLowerCase()) &&
      !selectedTags.includes(searchText.trim())
    ) {
      items.unshift({
        id: "create-option",
        text: `Create: "${searchText.trim()}"`,
        data: searchText.trim(),
        isCreateOption: true,
      });
    }

    return items;
  }, [availableOptions, selectedTags, searchText, allowCreate, showCheckboxes, hasDropdown]);

  /**
   * Toggle a tag on or off via the dropdown. For "Create" options the new tag is added
   * and the search text is cleared. For regular items the tag is added when unchecked or
   * removed when already checked. The dropdown remains open after each interaction to
   * support multi-select without re-opening.
   */
  const handleToggleTag = useCallback(
    (item: DropdownItem) => {
      const tag = item.isCreateOption ? (item.data as string) : item.text;
      if (item.isCreateOption) {
        onSelectionChange([...selectedTags, tag]);
        setSearchText("");
      } else if (selectedTags.includes(tag)) {
        onSelectionChange(selectedTags.filter((t) => t !== tag));
      } else {
        onSelectionChange([...selectedTags, tag]);
      }
      // Dropdown intentionally stays open for multi-select
    },
    [selectedTags, onSelectionChange],
  );

  /**
   * Remove an individual tag via its chip close button.
   *
   * @param tagToRemove - The tag string value to remove from the selection.
   */
  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      onSelectionChange(selectedTags.filter((tag) => tag !== tagToRemove));
    },
    [selectedTags, onSelectionChange],
  );

  /**
   * Commits a free-text tag from the primary input. Trims whitespace,
   * rejects empty strings and duplicates, then appends the new tag.
   */
  const commitFreeText = useCallback(() => {
    const trimmed = searchText.trim();
    if (trimmed && !selectedTags.includes(trimmed)) {
      onSelectionChange([...selectedTags, trimmed]);
    }
    setSearchText("");
  }, [searchText, selectedTags, onSelectionChange]);

  /**
   * Commits the in-progress inline chip edit, updating or removing the tag.
   * If the edited value is empty, the tag is removed. If the edited value
   * duplicates another tag, the edit is rejected and the original value restored.
   */
  const commitEdit = useCallback(() => {
    /* istanbul ignore else */
    if (editingIndex !== null) {
      const trimmed = editValue.trim();
      const updated = [...selectedTags];
      if (trimmed) {
        // Reject edits that would create a duplicate (excluding the tag being edited)
        const isDuplicate = selectedTags.some(
          (tag, index) => index !== editingIndex && tag === trimmed,
        );
        if (!isDuplicate) {
          updated[editingIndex] = trimmed;
          onSelectionChange(updated);
        }
      } else {
        // Empty value removes the tag
        updated.splice(editingIndex, 1);
        onSelectionChange(updated);
      }
      setEditingIndex(null);
      setEditValue("");
    }
  }, [editingIndex, editValue, onSelectionChange, selectedTags]);

  /**
   * Cancels the in-progress inline chip edit, restoring the original value.
   */
  const cancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditValue("");
  }, []);

  /**
   * Open the dropdown (if available) and focus the search input when the
   * container area is clicked. Does nothing when editing a chip.
   */
  const handleContainerClick = useCallback(() => {
    /* istanbul ignore else */
    if (!effectivelyDisabled && editingIndex === null) {
      if (hasDropdown) {
        setShowDropdown(true);
      }
      inputRef.current?.focus();
    }
  }, [effectivelyDisabled, editingIndex, hasDropdown]);

  /**
   * Close the dropdown and reset search state. Called by DropdownPortal on
   * click-outside and Escape key.
   */
  const handleClose = useCallback(() => {
    setShowDropdown(false);
    setSearchText("");
    setSelectedIndex(-1);
  }, []);

  /**
   * Handles keydown events on the primary input. Manages Enter for free-text
   * creation, Escape for clearing, and delegates to existing dropdown keyboard
   * navigation when appropriate.
   *
   * @param e - The keyboard event from the primary input.
   */
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        // If the dropdown has a highlighted item, let DropdownPortal handle it
        if (hasDropdown && selectedIndex >= 0) {
          return;
        }
        // Otherwise, commit free-text if enabled
        if (automaticallyCreateOnReturn && searchText.trim()) {
          e.preventDefault();
          commitFreeText();
          // Re-focus the input for continuous entry
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      } else if (e.key === "Escape") {
        // In free-text-only mode, clear the input text
        /* istanbul ignore else */
        if (automaticallyCreateOnReturn) {
          setSearchText("");
        }
      }
    },
    [automaticallyCreateOnReturn, commitFreeText, hasDropdown, searchText, selectedIndex],
  );

  /**
   * Handles keydown events on the inline edit input.
   * Enter commits the edit; Escape cancels it.
   *
   * @param e - The keyboard event from the edit input.
   */
  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitEdit();
      } else if (e.key === "Escape") {
        cancelEdit();
      }
    },
    [commitEdit, cancelEdit],
  );

  /**
   * Handles blur on the primary input. In pure free-text mode (no dropdown),
   * commits the current text as a new tag.
   */
  const handleInputBlur = useCallback(() => {
    if (commitOnBlur && searchText.trim()) {
      commitFreeText();
    }
  }, [commitOnBlur, commitFreeText, searchText]);

  /**
   * Enters inline edit mode for a chip at the given index.
   *
   * @param index - The index of the tag to edit.
   * @param currentValue - The current text value of the tag.
   */
  const enterEditMode = useCallback(
    (index: number, currentValue: string) => {
      /* istanbul ignore else */
      if (allowEdit && !effectivelyDisabled) {
        setEditingIndex(index);
        setEditValue(currentValue);
      }
    },
    [allowEdit, effectivelyDisabled],
  );

  return (
    <div className={`tag-multiselect ${className ?? ""}`}>
      {/* Fixed-size, scrollable form-control container holds chips and the search input */}
      <div
        ref={containerRef}
        role="combobox"
        aria-expanded={showDropdown && !effectivelyDisabled && hasDropdown}
        aria-haspopup="listbox"
        className="form-control d-flex flex-wrap align-items-start gap-1 p-2"
        style={{
          height,
          overflowY: "auto",
          overflowX: "auto",
          cursor: effectivelyDisabled ? "default" : "text",
          backgroundColor: effectivelyDisabled ? "var(--bs-secondary-bg)" : undefined,
        }}
        onClick={handleContainerClick}
        onMouseDown={(e) => {
          // Prevent container clicks from blurring the inline edit input
          if (editingIndex !== null) {
            e.preventDefault();
          }
        }}
      >
        {/* Inline chip badges (or edit input when editing) */}
        {selectedTags.map((tag, index) =>
          allowEdit && !effectivelyDisabled && editingIndex === index ? (
            <input
              key={`edit-${index}`}
              type="text"
              className={`border rounded px-1 ${styles.editInput}`}
              value={editValue}
              autoFocus
              onChange={(e) => {
                setEditValue(e.target.value);
              }}
              onBlur={commitEdit}
              onKeyDown={handleEditKeyDown}
              aria-label={`Edit tag: ${tag}`}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <Badge
              key={tag}
              bg="secondary"
              className={`d-flex align-items-center ${styles.chip} ${effectivelyDisabled ? styles.chipDisabled : styles.chipClickable}`}
              onClick={(e) => {
                /* istanbul ignore else */
                if (!effectivelyDisabled) {
                  e.stopPropagation();
                  if (allowEdit) {
                    enterEditMode(index, tag);
                  }
                }
              }}
            >
              <span>{tag}</span>
              {!effectivelyDisabled && (
                <button
                  type="button"
                  className="btn-close btn-close-white ms-1"
                  style={{ fontSize: "0.6em" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTag(tag);
                  }}
                  aria-label={`Remove ${tag}`}
                />
              )}
            </Badge>
          ),
        )}

        {/* Primary input — serves as search (dropdown mode) or free-text entry */}
        {!effectivelyDisabled && (
          <input
            ref={inputRef}
            type="text"
            className={`border-0 flex-grow-1 ${styles.tagInput}`}
            placeholder={selectedTags.length === 0 ? placeholder : ""}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              if (hasDropdown && !showDropdown) {
                setShowDropdown(true);
              }
            }}
            onFocus={() => {
              if (hasDropdown) {
                setShowDropdown(true);
              }
            }}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            disabled={effectivelyDisabled}
            autoComplete="off"
            aria-label={inputAriaLabel}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>

      {/* Dropdown portal — only rendered when dropdown content is available */}
      {hasDropdown && (
        <DropdownPortal
          isOpen={showDropdown && dropdownItems.length > 0 && !effectivelyDisabled}
          items={dropdownItems}
          onSelect={handleToggleTag}
          onClose={handleClose}
          anchorRef={containerRef}
          selectedIndex={selectedIndex}
          onSelectedIndexChange={setSelectedIndex}
        />
      )}
    </div>
  );
};

ControlTags.displayName = "ControlTags";
